import { useRef, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PdpSharedVimeoVideoLazy } from '../../../shared/PdpSharedVimeoVideo/PdpSharedVimeoVideoLazy.tsx';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import { useFullscreen } from 'dibs-elements/exports/useFullscreen.tsx';
import { getBuyerId } from 'dibs-cookie-jar';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { type VideoContainer_videoVimeo$key } from './__generated__/VideoContainer_videoVimeo.graphql.ts';

const videoVimeoFragment = graphql`
    fragment VideoContainer_videoVimeo on VideoVimeo {
        vimeoId
    }
`;

export const VideoContainer: FC<{
    videoVimeo?: VideoContainer_videoVimeo$key | null;
    vimeoItemsVideoHLSPlayerFF?: boolean;
    muxMonitoringFF?: boolean;
    videoUrl: string;
    heightOffset?: number;
    paused?: boolean;
    isMobile?: boolean;
    hideControls?: boolean;
    poster?: string;
    width?: number | null;
    height?: number | null;
}> = ({
    videoVimeo: videoVimeoRef,
    vimeoItemsVideoHLSPlayerFF,
    muxMonitoringFF = false,
    videoUrl,
    heightOffset,
    paused = false,
    isMobile = false,
    hideControls = false,
    poster,
    width,
    height,
}) => {
    const videoVimeoData = useFragment(videoVimeoFragment, videoVimeoRef);
    const videoPlayerRef = useRef<HTMLVideoElement>(null);
    const { handleFullscreenToggle } = useFullscreen(videoPlayerRef);
    const aspectRatio = width && height ? width / height : undefined;
    const isClient = useClientState();
    const viewerUserId = isClient ? getBuyerId(document.cookie) : '';

    if (videoVimeoData?.vimeoId && !vimeoItemsVideoHLSPlayerFF) {
        return (
            <PdpSharedVimeoVideoLazy
                id={videoVimeoData.vimeoId}
                useDefaultControls={!isMobile}
                heightOffset={heightOffset}
                paused={paused}
            />
        );
    } else {
        return (
            <div
                className={classnames(
                    dibsCss.wFull,
                    dibsCss.minH0,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    {
                        [dibsCss.maxHFull]: !!aspectRatio,
                        [dibsCss.hFull]: !aspectRatio,
                    }
                )}
                style={{ aspectRatio }}
            >
                <VideoPlayer ref={videoPlayerRef} poster={poster} width={width} height={height}>
                    <VideoPlayer.Media
                        src={videoUrl}
                        playback={{ loop: true, autoPlay: !paused }}
                        callbacks={{
                            onClick: () => {
                                if (videoPlayerRef.current && isMobile) {
                                    handleFullscreenToggle();
                                }
                            },
                        }}
                        monitor={muxMonitoringFF}
                        monitorMetadata={{ viewerUserId }}
                    />
                    {!hideControls && <VideoPlayer.Logo />}
                    {!isMobile && !hideControls && <VideoPlayer.Controls />}
                </VideoPlayer>
            </div>
        );
    }
};
